<template>
  <div>
    <!-- One line, the spot flow's filter panel idiom: a labelled control group per `col-auto`, the
         running commentary in the `col` that takes what is left (FIX4-2 point 1). -->
    <div class="row g-2 align-items-end" data-testid="range-row">
      <div class="col-auto range-width">
        <label class="form-label mb-1">
          {{ $t("forwards.picker.period.title") }}
        </label>
        <SegmentedControl v-model="width" :options="widthOptions" />
      </div>

      <div class="col-auto">
        <label class="form-label fs-10 mb-1" :for="fromId">
          {{ $t("forwards.picker.period.from") }}
        </label>
        <select
          :id="fromId"
          v-model="from"
          class="form-select form-select-sm range-select"
          data-testid="range-from"
        >
          <option v-for="label in options" :key="label" :value="label">
            {{ label }}
          </option>
        </select>
      </div>

      <div class="col-auto">
        <label class="form-label fs-10 mb-1" :for="untilId">
          {{ $t("forwards.picker.period.until") }}
        </label>
        <select
          :id="untilId"
          v-model="until"
          class="form-select form-select-sm range-select"
          data-testid="range-until"
        >
          <option v-for="label in options" :key="label" :value="label">
            {{ label }}
          </option>
        </select>
      </div>

      <div class="col align-self-center">
        <p
          v-if="periods.length === 0"
          class="error-note mb-0"
          role="alert"
          data-testid="range-invalid"
        >
          {{ $t("forwards.picker.period.invalid_range") }}
        </p>
        <p v-else class="text-body-tertiary fs-10 mb-0">
          {{
            $t("forwards.picker.period.selected", {
              count: periods.length,
              periods: periods.join(", "),
            })
          }}
        </p>
      </div>
    </div>

    <div v-if="widthNote" class="advisory-note mt-2">
      <FontAwesomeIcon icon="fa-solid fa-circle-info" />
      <span>{{ widthNote }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import SegmentedControl from "@/components/common/SegmentedControl.vue";
import type { PeriodWidth } from "@/forwards2/periodUtils";
import {
  periodOptions,
  periodRange,
  type PickerPeriodForm,
} from "@/forwards2/picker/pickerForm";
import { computed, useId } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The production periods the deal sells — the first block of the picker's filter area.
 *
 * One width for the whole range — the picker endpoint refuses a mixture, because "which pools can
 * back this" has no single answer across widths. The width is the vintage the deal sells: the four
 * quarters of next year are the default, since most contracts sell each quarter's production; a year
 * or half-year pick still delivers after each quarter where a pool allows, which the note for those
 * widths explains. A month can only be backed where the registry issues monthly statements, which is
 * the month warning.
 */
const props = withDefaults(
  defineProps<{
    /** The widths offered, in order; a buyer-facing page leaves months out. */
    widths?: readonly PeriodWidth[];
  }>(),
  { widths: () => ["year", "half_year", "quarter", "month"] }
);

const form = defineModel<PickerPeriodForm>({ required: true });

const { t } = useI18n();

const fromId = useId();
const untilId = useId();

const options = computed(() => periodOptions(form.value.width));
const periods = computed(() => periodRange(form.value));

const widthOptions = computed(() =>
  props.widths.map((value) => ({
    value,
    label: t(`forwards.common.period_width.${value}`),
  }))
);

const widthNote = computed(() => {
  if (form.value.width === "month") {
    return t("forwards.picker.period.month_warning");
  }
  if (form.value.width === "year") {
    return t("forwards.picker.period.year_warning");
  }
  if (form.value.width === "half_year") {
    return t("forwards.picker.period.half_year_warning");
  }
  return null;
});

const width = computed<PeriodWidth>({
  get: () => form.value.width,
  set: (value) => {
    // The labels of one width mean nothing in another, so the range restarts at the equivalent
    // window rather than carrying over an unparseable pair.
    const labels = periodOptions(value);
    const start =
      labels[Math.min(FIRST_FUTURE_OPTION[value], labels.length - 1)];
    form.value = {
      width: value,
      from: start,
      until:
        labels[
          Math.min(
            labels.indexOf(start) + DEFAULT_SPAN[value],
            labels.length - 1
          )
        ],
    };
  },
});

const from = computed<string>({
  get: () => form.value.from,
  set: (value) => {
    form.value = { ...form.value, from: value };
  },
});

const until = computed<string>({
  get: () => form.value.until,
  set: (value) => {
    form.value = { ...form.value, until: value };
  },
});

/** Index of the first period after the current one in `periodOptions` — the past is behind it. */
const FIRST_FUTURE_OPTION: Record<PeriodWidth, number> = {
  month: 13,
  quarter: 5,
  half_year: 3,
  year: 2,
};

/** How many periods after the first the range ends on by default. */
const DEFAULT_SPAN: Record<PeriodWidth, number> = {
  month: 11,
  quarter: 3,
  half_year: 1,
  year: 0,
};
</script>

<style scoped lang="scss">
// SegmentedControl fills its container by design (it is a page-level view switcher elsewhere).
// Here it is one control among several on a filter line, so it stays the size of its labels
// (D-FIX42-02).
.range-width :deep(.segmented-control) {
  width: auto;
}

// Fixed rather than shrink-wrapped: the option labels change with the period width
// ("2026-Q3" / "2026-12" / "2027"), and a select that resizes would shift the whole line
// every time the width is switched (D-FIX42-03).
.range-select {
  width: 9rem;
}
</style>
